import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Collapse,
  IconButton,
  Alert,
  Stack,
  Grid,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CardMembership as CardMembershipIcon,
  FitnessCenter as FitnessCenterIcon,
  CalendarToday as CalendarTodayIcon,
  ShoppingCart as ShoppingCartIcon,
  AllInclusive as AllInclusiveIcon,
} from "@mui/icons-material";
import { useAuthStore } from "../../store";
import StoreModal from "../StoreModal";

const AllPassesTable = () => {
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});

  const { passes } = useAuthStore((state) => ({
    passes: state.passes,
  }));

  const handleOpenStoreModal = () => {
    setStoreModalOpen(true);
  };

  const toggleCardExpansion = (passId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [passId]: !prev[passId],
    }));
  };

  // Calculate pass status
  const getPassStatus = (validUntil) => {
    const now = new Date();
    const expiryDate = new Date(validUntil);
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Wygasł', color: 'error', days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expiring', label: 'Wygasa wkrótce', color: 'warning', days: daysUntilExpiry };
    } else {
      return { status: 'active', label: 'Aktywny', color: 'success', days: daysUntilExpiry };
    }
  };

  // Format expiry date with relative time
  const formatExpiryDate = (validUntil) => {
    const expiryDate = new Date(validUntil);
    const status = getPassStatus(validUntil);
    
    const formattedDate = expiryDate.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    if (status.days < 0) {
      return `${formattedDate} (wygasł ${Math.abs(status.days)} dni temu)`;
    } else if (status.days === 0) {
      return `${formattedDate} (wygasa dziś!)`;
    } else if (status.days === 1) {
      return `${formattedDate} (wygasa jutro!)`;
    } else if (status.days <= 7) {
      return `${formattedDate} (za ${status.days} dni)`;
    } else {
      return formattedDate;
    }
  };

  // Calculate total remaining lessons
  const getTotalRemainingLessons = (lessonCategories) => {
    const hasUnlimited = lessonCategories.some(l => l.is_unlimited);
    if (hasUnlimited) return 'unlimited';
    
    return lessonCategories.reduce((sum, lesson) => sum + lesson.remaining_lessons, 0);
  };

  // Group and sort passes by status
  const groupedPasses = useMemo(() => {
    if (!Array.isArray(passes)) return { active: [], expiring: [], expired: [] };

    const groups = { active: [], expiring: [], expired: [] };
    
    passes.forEach(pass => {
      const status = getPassStatus(pass.valid_until);
      groups[status.status].push(pass);
    });

    return groups;
  }, [passes]);

  // Render a single pass card
  const renderPassCard = (pass, index) => {
    const status = getPassStatus(pass.valid_until);
    const isExpanded = expandedCards[pass.id] || false;
    const totalRemaining = getTotalRemainingLessons(pass.lesson_categories_for_pass);
    const hasMultipleCategories = pass.lesson_categories_for_pass.length > 1;

    return (
      <Card
        key={index}
        sx={{
          mb: 2,
          borderLeft: 4,
          borderLeftColor: `${status.color}.main`,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-2px)',
          },
          opacity: status.status === 'expired' ? 0.7 : 1,
        }}
      >
        <CardContent>
          {/* Card Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <CardMembershipIcon color="primary" />
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                {pass.pass_type_name}
              </Typography>
            </Box>
            <Chip
              label={status.label}
              color={status.color}
              size="small"
              icon={status.status === 'active' ? <CheckCircleIcon /> : <WarningIcon />}
            />
          </Box>

          {/* Quick Stats */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Ważny do
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatExpiryDate(pass.valid_until)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FitnessCenterIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Pozostało zajęć
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {totalRemaining === 'unlimited' ? (
                      <>
                        <AllInclusiveIcon fontSize="small" />
                        Bez limitu
                      </>
                    ) : (
                      totalRemaining
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Lesson Categories Preview */}
          {hasMultipleCategories && !isExpanded && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {pass.lesson_categories_for_pass.length} typów zajęć
            </Typography>
          )}

          {/* Expandable Details */}
          {hasMultipleCategories && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <IconButton
                onClick={() => toggleCardExpansion(pass.id)}
                size="small"
                sx={{ 
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.light', color: 'primary.contrastText' }
                }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                  {isExpanded ? 'Ukryj szczegóły' : 'Pokaż szczegóły'}
                </Typography>
              </IconButton>
            </Box>
          )}

          <Collapse in={isExpanded || !hasMultipleCategories}>
            <Box sx={{ mt: 2 }}>
              {pass.lesson_categories_for_pass.map((lesson, idx) => {
                const progress = lesson.is_unlimited 
                  ? 100 
                  : (lesson.consumed_lessons / (lesson.consumed_lessons + lesson.remaining_lessons)) * 100;
                
                return (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: idx < pass.lesson_categories_for_pass.length - 1 ? 1.5 : 0,
                      backgroundColor: 'grey.50',
                      borderLeft: 3,
                      borderLeftColor: 'primary.light',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {lesson.name}
                    </Typography>
                    
                    {lesson.is_unlimited ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <AllInclusiveIcon color="primary" fontSize="small" />
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                          Bez limitu zajęć
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Wykorzystano: {lesson.consumed_lessons} | Pozostało: {lesson.remaining_lessons}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {Math.round(progress)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              backgroundColor: lesson.remaining_lessons === 0 ? 'error.main' : 'success.main',
                            },
                          }}
                        />
                      </>
                    )}
                  </Paper>
                );
              })}
            </Box>
          </Collapse>

          {/* Warning for expiring passes */}
          {status.status === 'expiring' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Ten karnet wygasa za {status.days} {status.days === 1 ? 'dzień' : 'dni'}!
            </Alert>
          )}

          {/* Warning for expired passes */}
          {status.status === 'expired' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Ten karnet wygasł. Kup nowy karnet, aby kontynuować zajęcia.
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  // Empty state
  if (!passes || passes.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CardMembershipIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Nie masz jeszcze żadnych karnetów
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Kup karnet, aby móc zapisywać się na zajęcia
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<ShoppingCartIcon />}
          onClick={handleOpenStoreModal}
        >
          Kup karnet
        </Button>
        <StoreModal
          open={storeModalOpen}
          onClose={() => setStoreModalOpen(false)}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* Active Passes */}
      {groupedPasses.active.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
            Aktywne karnety ({groupedPasses.active.length})
          </Typography>
          <Stack spacing={2}>
            {groupedPasses.active.map((pass, index) => renderPassCard(pass, index))}
          </Stack>
        </Box>
      )}

      {/* Expiring Soon Passes */}
      {groupedPasses.expiring.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
            Karnety wygasające wkrótce ({groupedPasses.expiring.length})
          </Typography>
          <Stack spacing={2}>
            {groupedPasses.expiring.map((pass, index) => renderPassCard(pass, index))}
          </Stack>
        </Box>
      )}

      {/* Expired Passes */}
      {groupedPasses.expired.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
            Wygasłe karnety ({groupedPasses.expired.length})
          </Typography>
          <Stack spacing={2}>
            {groupedPasses.expired.map((pass, index) => renderPassCard(pass, index))}
          </Stack>
        </Box>
      )}

      {/* Buy Pass Button */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<ShoppingCartIcon />}
          onClick={handleOpenStoreModal}
          sx={{ minWidth: 200 }}
        >
          Kup nowy karnet
        </Button>
      </Box>

      <StoreModal
        open={storeModalOpen}
        onClose={() => setStoreModalOpen(false)}
      />
    </Box>
  );
};

export default AllPassesTable;
